import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Permission, type FeedbackArt } from '@brandschutz/shared';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input, Textarea } from '@/components/ui/Input';
import { Icon } from '@/lib/icons';
import { useToast } from '@/components/ui/Toast';
import { useCanI } from '@/lib/rbac';
import { cn } from '@/lib/cn';
import { meldeFeedback } from './api';

/**
 * Rückmeldung aus der laufenden Anwendung.
 *
 * Steht global in der Kopfzeile, weil Fehler dort auffallen, wo man gerade
 * arbeitet — und nicht dort, wo ein Meldeformular liegt. Die aktuelle Seite
 * wird automatisch mitgeschickt; das spart die Rückfrage „wo genau war das?".
 *
 * Das Textfeld ist das normale `Textarea` und bringt damit den Diktatknopf
 * mit: reinschreiben oder reinsprechen, ohne eigene Sonderlösung.
 *
 * Nicht für alle: eine Meldung wird zum Vorgang im Entwicklungs-Repository.
 * Wer nur lesend zuschaut, bekommt den Knopf deshalb gar nicht erst zu sehen —
 * durchgesetzt wird das ohnehin in der Schnittstelle.
 */
export function FeedbackTrigger() {
  const darfMelden = useCanI(Permission.FEEDBACK_SUBMIT);
  const [offen, setOffen] = useState(false);
  const [art, setArt] = useState<FeedbackArt>('fehler');
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const toast = useToast();
  const location = useLocation();

  const schliessen = (): void => {
    setOffen(false);
    setTitel('');
    setBeschreibung('');
    setArt('fehler');
  };

  const senden = useMutation({
    mutationFn: () =>
      meldeFeedback({
        art,
        titel: titel.trim(),
        beschreibung: beschreibung.trim(),
        seite: location.pathname,
      }),
    onSuccess: (m) => {
      schliessen();
      // Ehrlich unterscheiden: angelegt heißt angelegt, gespeichert heißt
      // gespeichert. Ein „Danke!" bei fehlender Anbindung wäre eine
      // Behauptung, die niemand einlöst.
      toast.success(
        m.status === 'uebermittelt' ? 'Meldung als Issue angelegt' : 'Meldung gespeichert',
        m.status === 'uebermittelt'
          ? `Nummer ${m.githubNummer}. Danke!`
          : 'Sie wird übermittelt, sobald die Anbindung steht. Danke!',
      );
    },
    onError: () => toast.error('Meldung konnte nicht gespeichert werden'),
  });

  const gueltig = titel.trim().length >= 5 && beschreibung.trim().length >= 10;

  if (!darfMelden) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOffen(true)}
        iconLeft={<Icon name="MessageSquare" size={16} />}
        aria-label="Fehler oder Wunsch melden"
        title="Fehler oder Wunsch melden"
      >
        <span className="hidden sm:inline">Feedback</span>
      </Button>

      <Modal
        open={offen}
        onClose={schliessen}
        title="Fehler oder Wunsch melden"
        description="Was ist Ihnen aufgefallen? Die aktuelle Seite wird automatisch mitgeschickt."
      >
        <form
          className="space-y-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (gueltig) senden.mutate();
          }}
        >
          <div
            role="radiogroup"
            aria-label="Art der Meldung"
            className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-paper p-1"
          >
            {(
              [
                ['fehler', 'Etwas funktioniert nicht', 'AlertTriangle'],
                ['wunsch', 'Etwas fehlt mir', 'Sparkles'],
              ] as const
            ).map(([wert, label, icon]) => (
              <button
                key={wert}
                type="button"
                role="radio"
                aria-checked={art === wert}
                onClick={() => setArt(wert)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors',
                  art === wert
                    ? 'bg-accent-500 text-ink-950'
                    : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
                )}
              >
                <Icon name={icon} size={13} />
                {label}
              </button>
            ))}
          </div>

          <Input
            label="Kurz gesagt"
            value={titel}
            onChange={(ev) => setTitel(ev.target.value)}
            placeholder={
              art === 'fehler'
                ? 'z. B. Frist wird nach dem Abschluss weitergezählt'
                : 'z. B. Löschwassermenge aus dem Hydrantenplan übernehmen'
            }
            maxLength={150}
            required
          />

          <Textarea
            label="Ausführlich"
            value={beschreibung}
            onChange={(ev) => setBeschreibung(ev.target.value)}
            rows={5}
            placeholder={
              art === 'fehler'
                ? 'Was haben Sie getan, was ist passiert, was hätten Sie erwartet?'
                : 'Was möchten Sie tun können — und was hindert Sie heute daran?'
            }
            // Erst mit einem KI-Kontext blendet das Textfeld seine
            // Werkzeugleiste ein — und damit den Diktatknopf. Ohne ihn stand
            // der Hinweis „Sie koennen diktieren" ueber einem Feld, das gar
            // kein Mikrofon anbot.
            //
            // Kein Hinweistext mehr, der die Werkzeuge ankuendigt: welche
            // davon erscheinen, haengt am Diktat-Feature-Flag. Die Knoepfe
            // tragen eigene Beschriftungen — was da steht, gibt es auch.
            aiContextHint={
              art === 'fehler'
                ? 'Fehlermeldung eines Anwenders zu einer Brandschutz-Software'
                : 'Verbesserungswunsch eines Anwenders zu einer Brandschutz-Software'
            }
            maxLength={5000}
            required
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={schliessen}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!gueltig} loading={senden.isPending}>
              Absenden
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
